// Shapes returned by GET /reports/epics/:issueKey/summary, plus the colours the panel uses.

export type StatusGroup = 'todo' | 'in_progress' | 'on_hold' | 'done' | 'invalid';
export type GroupCounts = Record<StatusGroup, number>;

export interface LogTypeHours {
  /** null means the worklogs carry no tag. */
  log_type: string | null;
  hours: number;
}

export interface EpicSummaryItem {
  issue_key: string;
  name: string;
  issue_type: string;
  is_subtask: boolean;
  parent_key: string | null;
  status: string;
  group: StatusGroup;
  assignee: { account_id: string; name: string } | null;
  hours_period: number;
  hours_to_date: number;
}

export interface EpicSummaryPerson {
  account_id: string;
  name: string;
  hours_period: number;
  hours_to_date: number;
  assigned: number;
  by_log_type: LogTypeHours[];
}

export interface EpicSummary {
  epic: {
    issue_key: string;
    project_key: string;
    project_name: string | null;
    name: string;
    description: string;
    priority: string | null;
    status_now: string;
    status_at_period_end: string | null;
    created_on: string | null;
  };
  period: { key: string | null; from: string; to: string; as_of: string | null; is_current: boolean };
  counts: {
    tickets: { total: number; counted: number; by_group: GroupCounts; progress_pct: number | null };
    subtasks: { total: number; counted: number; by_group: GroupCounts };
    by_type: { issue_type: string; is_subtask: boolean; count: number }[];
    by_status: { status: string; group: StatusGroup; count: number }[];
  };
  hours: { period: number; to_date: number; on_epic_itself: number; people: number; by_log_type: LogTypeHours[] };
  people: EpicSummaryPerson[];
  activity: { first_at: string | null; last_at: string | null; days_since_last: number | null };
  timeline: { status: string; at: string }[];
  items: EpicSummaryItem[];
  narrative: { context: string | null; lines: string[] };
}

/**
 * What a page hands the panel when a row is opened. The Reports page names its period ("2026-10",
 * "2026-Q4", "2026") so the panel reads the same instants as the list; the Planning page passes
 * the range of its quarter.
 */
export type EpicTarget = {
  issueKey: string;
  projectName: string;
  title: string;
  periodLabel: string;
} & ({ period: string } | { from: string; to: string });

// Order of the segments in the progress bar and of the chips under it.
export const GROUP_ORDER: StatusGroup[] = ['done', 'in_progress', 'on_hold', 'todo', 'invalid'];

export const GROUP_STYLE: Record<StatusGroup, { label: string; dot: string; bar: string; chip: string; chipOn: string }> = {
  done: {
    label: 'Done',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    chip: 'bg-white text-gray-700 ring-gray-200 hover:ring-gray-300',
    chipOn: 'bg-emerald-50 text-emerald-800 ring-emerald-500',
  },
  in_progress: {
    label: 'In progress',
    dot: 'bg-blue-500',
    bar: 'bg-blue-500',
    chip: 'bg-white text-gray-700 ring-gray-200 hover:ring-gray-300',
    chipOn: 'bg-blue-50 text-blue-800 ring-blue-500',
  },
  on_hold: {
    label: 'On hold',
    dot: 'bg-rose-500',
    bar: 'bg-rose-400',
    chip: 'bg-white text-gray-700 ring-gray-200 hover:ring-gray-300',
    chipOn: 'bg-rose-50 text-rose-800 ring-rose-500',
  },
  todo: {
    label: 'Not started',
    dot: 'bg-gray-400',
    bar: 'bg-gray-300',
    chip: 'bg-white text-gray-700 ring-gray-200 hover:ring-gray-300',
    chipOn: 'bg-gray-100 text-gray-800 ring-gray-500',
  },
  invalid: {
    label: 'Invalid',
    dot: 'bg-slate-300',
    bar: 'bg-slate-300',
    chip: 'bg-white text-gray-500 ring-gray-200 hover:ring-gray-300',
    chipOn: 'bg-slate-100 text-slate-700 ring-slate-400',
  },
};
